import { AlertTriangle } from 'lucide-react'
import React, { Component, type ReactNode } from 'react'

import WideButton from './button/WideButton'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className='min-h-screen bg-gradient-to-r from-amber-400 to-yellow-300 flex items-center justify-center p-4'>
          <div className='max-w-md w-full text-center'>
            {/* 벌 캐릭터 */}
            <div className='flex justify-center mb-8'>
              <div className='flex items-center gap-2'>
                <div className='relative'>
                  {/* 벌 머리 */}
                  <div className='w-24 h-24 bg-amber-600 rounded-full relative'>
                    {/* 눈 - 크게 뜬 당황한 표정 */}
                    <div className='absolute top-4 left-4 w-4 h-4 bg-white rounded-full'></div>
                    <div className='absolute top-4 right-4 w-4 h-4 bg-white rounded-full'></div>
                    {/* 더듬이 */}
                    <div className='absolute -top-4 left-4 w-2 h-4 bg-amber-800 rounded-full'></div>
                    <div className='absolute -top-4 right-4 w-2 h-4 bg-amber-800 rounded-full'></div>
                  </div>
                  {/* 벌 몸통 */}
                  <div className='absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-16 h-12 bg-amber-500 rounded-full'>
                    {/* 검은 줄무늬 */}
                    <div className='absolute top-0 left-0 w-full h-2 bg-amber-900 rounded-full'></div>
                    <div className='absolute bottom-0 left-0 w-full h-2 bg-amber-900 rounded-full'></div>
                  </div>
                  {/* 날개 */}
                  <div className='absolute -top-2 -left-4 w-8 h-6 bg-white/70 rounded-full transform rotate-12'></div>
                  <div className='absolute -top-2 -right-4 w-8 h-6 bg-white/70 rounded-full transform -rotate-12'></div>
                </div>
              </div>
            </div>

            {/* 에러 메시지 */}
            <div className='bg-white/90 backdrop-blur rounded-2xl p-6 mb-6'>
              <div className='flex items-center justify-center gap-2 mb-3'>
                <AlertTriangle className='w-6 h-6 text-red-500' />
                <h1 className='text-2xl font-bold text-gray-800'>앱에 문제가 발생했어요</h1>
              </div>
              <p className='text-gray-600 leading-relaxed mb-4'>
                예상치 못한 오류가 발생했습니다.
                <br />
                페이지를 새로고침하거나 잠시 후 다시 시도해 주세요.
              </p>

              {/* 개발환경에서만 에러 상세 정보 표시 */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className='mt-4 text-left'>
                  <summary className='cursor-pointer text-sm text-gray-500 mb-2'>에러 상세 정보</summary>
                  <div className='bg-gray-100 p-3 rounded-lg text-xs font-mono text-gray-700 overflow-auto'>
                    <div className='mb-2'>
                      <strong>Error:</strong> {this.state.error.message || '알 수 없는 오류'}
                    </div>
                    {this.state.error.stack && (
                      <div>
                        <strong>Stack:</strong>
                        <pre className='whitespace-pre-wrap mt-1'>{this.state.error.stack}</pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>

            {/* 버튼들 */}
            <div className='space-y-3'>
              <WideButton
                handleClick={() => window.location.reload()}
                buttonText='새로고침'
                className='flex items-center justify-center gap-2'
              />
              <WideButton
                handleClick={() => (window.location.href = '/')}
                buttonText='홈으로 가기'
                className='bg-amber-500 hover:bg-amber-600 text-white border-amber-500 flex items-center justify-center gap-2'
              />
            </div>

            {/* 도움말 */}
            <div className='mt-6 text-sm text-white/80'>
              <p>문제가 계속되면 고객센터로 문의해주세요</p>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
